<template>
  <common-template-saas-structure>
    <div class="dashboard">

      <div class="dashboard__overlay" />

      <layout-teleport-to-breadcrumb>
        <div class="dashboard__title animate__animated animate__flipInX ease-in-out a3 d1">
          Bienvenue <span class="text-capitalize">{{ `${store_account_details.prenom} ${store_account_details.nom}`.trim() }}</span>
        </div>
      </layout-teleport-to-breadcrumb>

      <div class="dashboard__main">

        <!-- Section Événements GHS -->
        <div class="dashboard__main__events">
          <b-badge class="dashboard__main__events__title animate__animated animate__fadeIn ease-in-out a3 d3">
            Événements GHS
          </b-badge>
          <div class="dashboard__main__events__content animate__animated animate__fadeIn ease-in-out a3 d3">
            <calendar-section class="dashboard__main__events__content__calendar" />
            <formations-section class="dashboard__main__events__content__formations" />
            <forum-section class="dashboard__main__events__content__forum" />

          </div>
        </div>
        <div class="dashboard__main__articles">
          <b-badge class="dashboard__main__articles__title animate__animated animate__fadeIn ease-in-out a3 d3">
            Actualités
          </b-badge>
          <ui-cloak
            :is-visible="fetchState.status.value === 'success'"
            :sustain-phase-duration="300"
          >
            <template #cloak>
              <div class="dashboard__main__articles__content animate__animated animate__fadeIn ease-in-out a3">
                <loading-section v-for="i in NB_ARTICLES" :key="i" />
              </div>
            </template>
            <template #default>
              <div class="dashboard__main__articles__content animate__animated animate__fadeIn ease-in-out a3 d2">
                <template v-if="articlesLoadingError">
                  <fallback-blog-section v-for="i in NB_ARTICLES" :key="i" />
                </template>
                <template v-else>
                  <blog-section v-for="(article, index) in articles" :key="index" :article-data="article" />
                </template>
              </div>
            </template>
          </ui-cloak>
        </div>
      </div>

      <footer class="dashboard__footer">
        <p class="dashboard__footer__text">
          Copyright 2026 &copy; ghs.fr
          4 rue d'Enghien, 75010 Paris
          01 53 34 25 25
        </p>
      </footer>
    </div>
  </common-template-saas-structure>
</template>
<script lang="ts">
import { useCompanyOptionalNavigationGuard } from '~/composables/layout/navigation'
import { useStore_account_details } from '~/stores/account/details'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import CalendarSection from '~/components/dashboard/CalendarSection.vue'
import FormationsSection from '~/components/dashboard/FormationsSection.vue'
import ForumSection from '~/components/dashboard/ForumSection.vue'
import BlogSection from '~/components/dashboard/BlogSection.vue'
import FallbackBlogSection from '~/components/dashboard/FallbackBlogSection.vue'
import LoadingSection from '~/components/dashboard/LoadingSection.vue'
import { useStore_dashboard_articles, NB_ARTICLES } from '~/stores/dashboard/articles'
import type { Article } from '~/stores/dashboard/articles'
import { useLegacyPageFetch } from '~/composables/core/hook'
import Console from '~/utils/lib/core/console'

const console = new Console('page:dashboard')

export default defineNuxtComponent({
  components: {
    CalendarSection,
    FormationsSection,
    BlogSection,
    FallbackBlogSection,
    LoadingSection,
    ForumSection,
  },
  setup() {
    const store_account_details = useStore_account_details()
    const store_dashboard_articles = useStore_dashboard_articles()

    useCompanyOptionalNavigationGuard()

    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_OPTIONAL,
      menuItem: MENU_ITEM.DASHBOARD,
      middleware: [
        'session-control-gate',
      ]
    })
    useHead({
      title: 'Accueil',
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    return {
      store_account_details,
      store_dashboard_articles,
      fetchState,
      fetchWrapper,
      NB_ARTICLES,
    }
  },
  data() {
    return {
      articles: [] as Article[],
      articlesLoadingError: false
    }
  },
  created() {
    this.fetchWrapper(
      () => this.getArticles()
    )
  },
  methods: {
    getArticles() {
      return this.store_dashboard_articles.getLatestArticles()
        .then((articles) => {
          this.articles = articles
        })
        .catch(() => {
          console.error('Error fetching articles')
          this.articlesLoadingError = true
        })
    }
  }
})
</script>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/dashboard/variables'

$dashboard-min-width: 900px

.dashboard
  z-index: z-index($z-indexes, 'main', 'floor')

  &__overlay
    @include virtual-content
    @include granular-overlay($z-index: -1)
    position: absolute


  padding: space-x(4)
  display: flex
  flex-direction: column
  min-height: calc(100vh - $header-simulated-height)
  position: relative
  justify-content: flex-start
  align-items: center


  &__title
    letter-spacing: 0.6px
    font-family: $font-family-heading-jumbo-light
    white-space: nowrap
    font-size: 24px
    line-height: 1.8
    padding-left: space-x(2)
    padding-right: space-x(2)
    color: $true-gray-95
    height: $header-simulated-height
    background: map-get($palette-layout, 'color-3')

  &__main
    display: flex
    min-width: $dashboard-min-width
    flex-direction: column
    gap: space-x(6)

    &__events

      &__title
        font-size: 115%
        font-family: $font-family-heading-jumbo-light
        color: $wine-berry
        background-color: mix-alpha($maize-crayola, 20%)
        margin-bottom: space-x(2)
        letter-spacing: 0.3px
        font-weight: $font-weight-normal
        padding: space-x(1) space-x(2)

      &__content
        display: grid
        grid-template-columns: 2fr 1fr 1.25fr
        gap: space-x(3)
        @include media('<1500px')
          grid-template-columns: 1fr 1fr
          grid-template-rows: auto auto

        &__calendar,
        &__formations,
        &__forum
          border: 1px solid $border-color
          border-radius: $border-radius-lg
          overflow: hidden

          @include media('<1500px')
            min-height: 200px

        &__calendar
          @include media('<1500px')
            grid-column: 1 / -1

        &__formations,
        &__forum
          @include media('<1500px')
            min-height: 150px
            max-height: 230px

    &__articles

      &__title
        font-size: 115%
        padding: space-x(1) space-x(2)
        font-family: $font-family-heading-jumbo-light
        color: $wine-berry
        background-color: mix-alpha($maize-crayola, 20%)
        margin-bottom: space-x(2)
        letter-spacing: 0.3px
        font-weight: $font-weight-normal

      &__content
        display: grid
        grid-template-columns: 1fr 1fr 1fr
        gap: space-x(3)

        @include media('>2000px')
          :deep(.blog-section)
            min-height: 480px

  &__footer
    text-align: center
    display: flex
    flex-direction: row
    align-items: center
    gap: space-x(2)
    margin-top: auto
    padding-top: space-x(6)

    &__text
      font-size: 12px
      line-height: 1.5
      font-family: $font-family-base-condensed
      font-weight: $font-weight-light
      color: $wine-berry

  ////////////////////

  .dashboard__main__events__content__calendar,
  .dashboard__main__events__content__formations,
  .dashboard__main__events__content__forum,
  :deep(.blog-section)
    // box-shadow: $shadow-discreet
    box-shadow: 0 0 4px 4px mix-alpha($primary, 4%)

</style>
